import {
  DetailsConfig,
  DetailsModelInterface,
  TravelDetailsInterface,
} from "interfaces/details";

const defaultModel: DetailsModelInterface = {
  basicFare: {
    amount: 0,
    currency: "EUR",
  },
  smartFare: {
    amount: 0,
    currency: "EUR",
  },
  conditions: false,
  origin: "",
  pool: "",
  numAdt: 1,
  numChd: 0,
  numInf: 0,
  minStay: 1,
  maxStay: 2,
  dayType: "",
  earliestOut: "",
  latestRet: "",
  timeRestriction: "",
  travelClass: "ECONOMY",
  isTravelingBusiness: false,
  poolDestinationsData: [],
  poolFromPrice: 0,
  imageFileName: "",
  availableStayValues: [],
  totalPrice: {
    amount: 0,
    currency: "EUR",
  },
  filterData: {
    maxStay: {
      start: 1,
      end: 11,
      titleKey: false,
    },
    minStay: {
      start: 1,
      end: 8,
      titleKey: false,
    },
    numAdt: {
      start: 1,
      end: 9,
      titleKey: false,
    },
    numChd: {
      start: 0,
      end: 8,
      titleKey: false,
    },
    numInf: {
      start: 0,
      end: 1,
      titleKey: false,
    },
    dayType: {
      values: ["FRI_SUN", "SAT_MON", "SATURDAY"],
      emptyTopLabel: "FULLFLEX",
    },
    timeRestriction: {
      values: ["NOTEARLY", "LATEFLIGHTS"],
      emptyTopLabel: "FULLFLEX",
    },
    travelClass: {
      values: ["ECONOMY", "BUSINESS"],
    },
  },
};

const defaultTravelDetails: TravelDetailsInterface = {
  hasTravelClass: true,
  departureBuffer: 0,
  returnBuffer: 2,
  maxPax: 9,
  minMaxDiff: 3,
  adtNumLabel: "numAdt_plural",
  chdNumLabel: "numChd_plural",
  infNumLabel: "numInf_plural",
  hasWeekendOptionsModal: false,
  calendarFixedHeight: true,
};

const defaultConfiguration = {
  model: {
    ...defaultModel,
    filterData: {
      ...defaultModel.filterData,
      numAdt: {
        ...defaultModel.filterData.numAdt,
        titleKey: "numAdt",
      },
      numChd: {
        ...defaultModel.filterData.numChd,
        titleKey: "numChd",
      },
      numInf: {
        ...defaultModel.filterData.numInf,
        titleKey: "numInf",
      },
    },
  },
  travelDetails: {
    ...defaultTravelDetails,
    adtNumLabel: undefined,
    chdNumLabel: undefined,
    infNumLabel: undefined,
  },
  alternateFlights: {
    hasPriceComparisonTable: true,
    hasPriceButton: false,
    url: (countryCode: string) => "",
  },
  yourSelection: {
    hasAsterisk: false,
  },
};

const ewgConfig: DetailsConfig[string] = {
  model: {
    ...defaultModel,
    minStay: 2,
    maxStay: 3,
    travelClass: "",
    filterData: {
      ...defaultModel.filterData,
      maxStay: {
        ...defaultModel.filterData.maxStay,
        end: 12,
      },
      minStay: {
        ...defaultModel.filterData.minStay,
        end: 9,
      },
    },
  },
  travelDetails: {
    hasTravelClass: false,
    departureBuffer: 0,
    returnBuffer: 7,
    maxPax: 9,
    minMaxDiff: 3,
    adtNumLabel: "numAdt_plural",
    chdNumLabel: "numChd_plural",
    infNumLabel: "numInf_plural",
    hasWeekendOptionsModal: true,
    calendarFixedHeight: false,
  },
  alternateFlights: {
    hasPriceComparisonTable: false,
    hasPriceButton: false,
    url: (countryCode: string) =>
      `https://www.eurowings.com/${countryCode}.html`,
  },
  yourSelection: {
    hasAsterisk: true,
  },
};

const configuration: DetailsConfig = {
  ewg: ewgConfig,
  lhs: {
    model: {
      ...defaultModel,
      filterData: {
        ...defaultModel.filterData,
        numAdt: {
          ...defaultModel.filterData.numAdt,
          titleKey: "numAdt",
        },
        numChd: {
          ...defaultModel.filterData.numChd,
          titleKey: "numChd",
        },
        numInf: {
          ...defaultModel.filterData.numInf,
          titleKey: "numInf",
        },
      },
    },
    travelDetails: {
      ...defaultTravelDetails,
      adtNumLabel: undefined,
      chdNumLabel: undefined,
      infNumLabel: undefined,
    },
    alternateFlights: {
      hasPriceComparisonTable: true,
      hasPriceButton: false,
      url: (countryCode: string) =>
        `https://www.lufthansa.com/de/${countryCode}/homepage`,
    },
    yourSelection: {
      hasAsterisk: false,
    },
  },
  oss: {
    model: defaultModel,
    travelDetails: {
      ...defaultTravelDetails,
    },
    alternateFlights: {
      hasPriceComparisonTable: true,
      hasPriceButton: true,
      url: (countryCode) =>
        `https://www.austrian.com/de/${countryCode}/homepage`,
    },
    yourSelection: {
      hasAsterisk: false,
    },
  },
  sns: {
    ...defaultConfiguration,
    travelDetails: {
      ...defaultTravelDetails,
      adtNumLabel: "numAdt_plural",
      chdNumLabel: "numChd_plural",
      infNumLabel: "numInf_plural",
    },
    alternateFlights: {
      ...defaultConfiguration.alternateFlights,
      hasPriceButton: true,
      url: (countryCode: string) => {
        let region = "be";
        if (["de", "en"].includes(countryCode)) {
          region = "de";
        }
        return `https://www.brusselsairlines.com/${region}/${countryCode}/homepage`;
      },
    },
    yourSelection: {
      hasAsterisk: false,
    },
    fieldOrder: [
      "travelClass",
      "numAdt",
      "numChd",
      "numInf",
      "dateRange",
      "timeRestriction",
      "dayType",
      "min-max-stay",
    ],
  },
  default: defaultConfiguration,
};

export default configuration;
